import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Fully offline, canned translation playground for the marketing page.
// No network calls — every translation below is pre-written so the demo is
// instant, free, and works logged-out. "Sign up" is the CTA for real text.

const LANGS = [
  { code: 'DE', name: 'German', flag: '🇩🇪' },
  { code: 'FR', name: 'French', flag: '🇫🇷' },
  { code: 'ES', name: 'Spanish', flag: '🇪🇸' },
  { code: 'IT', name: 'Italian', flag: '🇮🇹' },
  { code: 'PT', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'JA', name: 'Japanese', flag: '🇯🇵' },
];

// Each phrase: the English source, an optional glossary term, and a canned
// translation + locked glossary term per language. `tm` = memory-match score.
const PHRASES = [
  {
    source: 'Welcome to your workspace.',
    gloss: null,
    t: {
      DE: { text: 'Willkommen in Ihrem Workspace.', tm: 98, matches: 4 },
      FR: { text: 'Bienvenue dans votre espace de travail.', tm: 96, matches: 3 },
      ES: { text: 'Bienvenido a tu espacio de trabajo.', tm: 97, matches: 5 },
      IT: { text: 'Benvenuto nel tuo spazio di lavoro.', tm: 95, matches: 3 },
      PT: { text: 'Bem-vindo ao seu espaço de trabalho.', tm: 94, matches: 2 },
      JA: { text: 'ワークスペースへようこそ。', tm: 92, matches: 2 },
    },
  },
  {
    source: 'Rush Studio brings your entire localization pipeline into one calm dashboard.',
    gloss: 'localization',
    t: {
      DE: { text: 'Rush Studio vereint Ihre gesamte Lokalisierungs-Pipeline in einem ruhigen Dashboard.', term: 'Lokalisierungs', tm: 88, matches: 6 },
      FR: { text: 'Rush Studio réunit tout votre pipeline de localisation dans un tableau de bord épuré.', term: 'localisation', tm: 86, matches: 4 },
      ES: { text: 'Rush Studio reúne todo tu flujo de localización en un único panel tranquilo.', term: 'localización', tm: 87, matches: 5 },
      IT: { text: "Rush Studio riunisce l'intera pipeline di localizzazione in un'unica dashboard ordinata.", term: 'localizzazione', tm: 84, matches: 3 },
      PT: { text: 'O Rush Studio reúne todo o seu fluxo de localização num painel tranquilo.', term: 'localização', tm: 85, matches: 4 },
      JA: { text: 'Rush Studio は、ローカライズの全工程を一つの落ち着いたダッシュボードにまとめます。', term: 'ローカライズ', tm: 81, matches: 2 },
    },
  },
  {
    source: 'Your free trial starts today — no credit card required.',
    gloss: null,
    t: {
      DE: { text: 'Ihre kostenlose Testphase beginnt heute – keine Kreditkarte erforderlich.', tm: 91, matches: 3 },
      FR: { text: "Votre essai gratuit commence aujourd'hui — sans carte bancaire.", tm: 90, matches: 4 },
      ES: { text: 'Tu prueba gratuita empieza hoy: sin tarjeta de crédito.', tm: 93, matches: 5 },
      IT: { text: 'La tua prova gratuita inizia oggi, senza carta di credito.', tm: 89, matches: 3 },
      PT: { text: 'O seu teste gratuito começa hoje — sem cartão de crédito.', tm: 88, matches: 2 },
      JA: { text: '無料トライアルは今日から開始。クレジットカードは不要です。', tm: 84, matches: 2 },
    },
  },
  {
    source: 'Ship in 100+ languages without the tab graveyard.',
    gloss: null,
    t: {
      DE: { text: 'Veröffentlichen Sie in über 100 Sprachen – ohne Tab-Chaos.', tm: 82, matches: 2 },
      FR: { text: "Publiez dans plus de 100 langues, sans la jungle d'onglets.", tm: 80, matches: 2 },
      ES: { text: 'Publica en más de 100 idiomas, sin el caos de pestañas.', tm: 83, matches: 3 },
      IT: { text: 'Pubblica in oltre 100 lingue, senza il caos di schede.', tm: 79, matches: 2 },
      PT: { text: 'Publique em mais de 100 idiomas, sem o caos de abas.', tm: 81, matches: 2 },
      JA: { text: '100以上の言語で公開。タブの山とはお別れです。', tm: 76, matches: 1 },
    },
  },
];

// Wrap a glossary term inside a string with a highlighted <span>.
const withGloss = (text, term) => {
  if (!term) return text;
  const i = text.indexOf(term);
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="gloss">{text.slice(i, i + term.length)}</span>
      {text.slice(i + term.length)}
    </>
  );
};

const sameText = (a, b) => a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

const RushTryDemo = () => {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [source, setSource] = useState(PHRASES[0].source);
  const [lang, setLang] = useState('DE');
  const [status, setStatus] = useState('idle'); // idle | translating | done | unknown
  const [typed, setTyped] = useState('');
  const timers = useRef([]);

  const phrase = PHRASES.find((p) => sameText(p.source, source));
  const result = phrase ? phrase.t[lang] : null;
  const glossTerm = phrase && phrase.gloss ? (result && result.term) || null : null;

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => () => clearTimers(), []);

  const pickPhrase = (i) => {
    clearTimers();
    setPhraseIdx(i);
    setSource(PHRASES[i].source);
    setStatus('idle');
    setTyped('');
  };

  const translate = () => {
    clearTimers();
    if (!phrase || !result) {
      setStatus('unknown');
      setTyped('');
      return;
    }
    setStatus('translating');
    setTyped('');
    const full = result.text;
    // Reveal characters with a brief "thinking" pause first.
    timers.current.push(setTimeout(() => {
      const chars = Array.from(full);
      chars.forEach((_, i) => {
        timers.current.push(setTimeout(() => {
          setTyped(chars.slice(0, i + 1).join(''));
          if (i === chars.length - 1) setStatus('done');
        }, i * 22));
      });
    }, 520));
  };

  const activeLang = LANGS.find((l) => l.code === lang);

  return (
    <section className="section try-demo">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">Try it live</div>
          <h2>Translate a line.<br />Watch the editor work.</h2>
          <p className="section-lead">
            Pick a sentence, choose a language, and see exactly what your team sees — an AI draft with
            translation-memory matches and locked glossary terms. No sign-up, right here on the page.
          </p>
        </div>

        <div className="try-grid reveal reveal-scale">
          <div className="try-controls">
            <div className="try-label">1 · Pick a sentence</div>
            <div className="try-phrases">
              {PHRASES.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  className={`try-phrase ${i === phraseIdx && sameText(p.source, source) ? 'is-active' : ''}`}
                  onClick={() => pickPhrase(i)}
                >
                  {p.source}
                </button>
              ))}
            </div>

            <div className="try-label">2 · Or edit the source</div>
            <textarea
              className="try-input"
              value={source}
              spellCheck={false}
              onChange={(e) => {
                setSource(e.target.value);
                setStatus('idle');
                setTyped('');
              }}
              rows={2}
            />

            <div className="try-label">3 · Translate into</div>
            <div className="try-langs">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`lang-chip try-lang ${l.code === lang ? 'is-active' : ''}`}
                  onClick={() => {
                    setLang(l.code);
                    setStatus('idle');
                    setTyped('');
                  }}
                >
                  <span aria-hidden="true">{l.flag}</span> {l.name}
                </button>
              ))}
            </div>

            <button type="button" className="btn btn-accent try-go" onClick={translate}>
              {status === 'translating' ? 'Translating…' : 'Translate'}
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2 7.5h9M7 3l4 4.5L7 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="try-window product-window">
            <div className="product-chrome">
              <div className="traffic"><span /><span /><span /></div>
              <div className="url">rush.studio/editor · live preview</div>
            </div>
            <div className="editor try-editor">
              <div className="editor-h">
                <span className="file">demo · segment 1 / 1</span>
                <span className="meta">EN → {activeLang ? activeLang.name : lang}</span>
              </div>
              <div className="editor-body">
                <div className="editor-pane">
                  <div className="lbl">Source · English</div>
                  <p>{phrase && phrase.gloss ? withGloss(source, phrase.gloss) : source}</p>
                </div>
                <div className="editor-pane target">
                  <div className="lbl">Target · {activeLang ? activeLang.name : lang} · AI draft</div>

                  {status === 'idle' && (
                    <p className="try-placeholder">Press <b>Translate</b> to generate a draft.</p>
                  )}

                  {status === 'translating' && (
                    <p>{typed}<span className="try-caret" /></p>
                  )}

                  {status === 'done' && result && (
                    <>
                      <p>{withGloss(result.text, glossTerm)}</p>
                      <div className="try-meta">
                        <span className="try-tm">✓ {result.matches} TM match{result.matches === 1 ? '' : 'es'} · best {result.tm}%</span>
                        {glossTerm && <span className="try-lock">🔒 1 glossary term locked</span>}
                      </div>
                      <div className="editor-actions">
                        <button className="editor-action primary" type="button">Accept</button>
                        <button className="editor-action" type="button">Edit</button>
                        <button className="editor-action" type="button">Reject</button>
                      </div>
                    </>
                  )}

                  {status === 'unknown' && (
                    <div className="try-unknown">
                      <p className="try-placeholder">
                        This offline demo only ships a few sample sentences — but the real editor
                        translates anything you throw at it, in 100+ languages.
                      </p>
                      <Link to="/register" className="btn btn-accent" style={{ marginTop: 4 }}>
                        Translate your own text — free
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RushTryDemo;
